import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import startCase from 'lodash/startCase';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Checkbox from '@mui/material/Checkbox';
import TablePagination from '@mui/material/TablePagination';
import MKTable from 'components/MaterialKit/MKTable';
import MKBox from 'components/MaterialKit/MKBox';
import AnchorMenu from 'components/AnchorMenu';
import Button from 'components/Button';
import { withProps } from 'utils/hoc';
import TablePageActionButtonGroup from './TablePageActionButtonGroup';

const DataTable = ({ columns, data, idField, selectable, objectTypes, typeRouting, onSelectRow, onClickRow, onPressAdd, onPressEdit, onPressDelete, onPressCopy, onPressPaste, totalCount, currentPage, onPageChange, rowsPerPage, onRowsPerPageChange, rowsPerPageOptions, paginationDisabled, sortBy, sortDirection, onSort, pageButtonProps, addButtonLabel, editButtonLabel, deleteButtonLabel, copyButtonLabel, pasteButtonLabel, ...props }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const navigate = useNavigate();

  const onOpenMenu = useCallback(({ currentTarget }) => setMenuAnchor(currentTarget), []);

  const selectedItem = useMemo(() => (data || []).find(
    (datum) => datum[idField] === selectedId,
  ), [data, idField, selectedId]);

  const headers = useMemo(() => [
    ...(selectable ? [{
      name: 'selected',
      label: '',
      width: '0',
    }] : []),
    ...columns.map((column, i) => {
      const { label, field, ...colProps } = column;
      let arrowIcon;

      if (sortDirection === 'asc') {
        arrowIcon = 'arrow_drop_up';
      } else if (sortDirection === 'desc') {
        arrowIcon = 'arrow_drop_down';
      }
      return {
        name: field,
        label: (
          <Grid container justifyContent={i > 0 ? 'center' : 'flex-start'} alignItems="center" flexWrap="nowrap">
            {label !== undefined ? label : startCase(field)}
            {field === sortBy && (
              <Icon fontSize="default" color="dark">
                {arrowIcon}
              </Icon>
            )}
          </Grid>
        ),
        align: i > 0 ? 'center' : 'left',
        ...colProps,
      };
    }),
  ], [columns, selectable, sortBy, sortDirection]);

  const rows = useMemo(() => (data || []).map((datum) => {
    return columns.reduce((rowData, column) => {
      const { field, to, urlParams, href, formatter, content, renderContent, onClick } = column;
      const updatedRowData = { ...rowData };
      const displayText = formatter ? formatter(get(datum, field)) : get(datum, field);

      const parsedTo = (urlParams || []).reduce((result, urlParam) => {
        return result.replaceAll(`:${urlParam}`, get(datum, urlParam));
      }, to);

      if (typeof renderContent === 'function') {
        updatedRowData[field] = renderContent(datum);
      } else if (content) {
        updatedRowData[field] = content;
      } else if (to || href || onClick) {
        updatedRowData[field] = (
          <Button
            variant="text"
            onClick={() => (typeof onClick === 'function' ? onClick(datum) : null)}
            to={parsedTo}
            href={href}
            fullWidth
          >
            {displayText}
          </Button>
        );
      } else {
        updatedRowData[field] = displayText;
      }
      if (selectable) {
        updatedRowData.selected = (
          <Checkbox
            checked={selectedId === datum[idField]}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelectRow(datum[idField]);
              setSelectedId(e.target.checked ? datum[idField] : null);
            }}
          />
        );
      }
      return updatedRowData;
    }, {});
  }), [data, columns, idField, selectable, selectedId]);

  const actionButtons = useMemo(() => [
    ...(typeof onPressAdd === 'function' ? [{
      label: addButtonLabel || 'Add',
      color: 'success',
      onClick: objectTypes.length > 0 ? onOpenMenu : () => onPressAdd('default'),
    }] : []),
    ...(typeof onPressEdit === 'function' ? [{
      label: editButtonLabel || 'Edit',
      color: 'info',
      disabled: !selectedId,
      to: typeRouting[selectedItem?.type] || typeRouting?.default,
      onClick: () => onPressEdit(selectedId),
    }] : []),
    ...(typeof onPressDelete === 'function' ? [{
      label: deleteButtonLabel || 'Delete',
      color: 'error',
      disabled: !selectedId,
      onClick: () => {
        if (window.confirm('Do you confirm to delete this?')) {
          return Promise.resolve(onPressDelete(selectedId))
            .then(() => setSelectedId(null));
        }
        return Promise.resolve();
      },
    }] : []),
    ...(typeof onPressCopy === 'function' ? [{
      label: copyButtonLabel || 'Copy',
      color: 'dark',
      disabled: !selectedId,
      onClick: () => onPressCopy(selectedId),
    }] : []),
    ...(typeof onPressPaste === 'function' ? [{
      label: pasteButtonLabel || 'Paste',
      color: 'secondary',
      onClick: onPressPaste,
    }] : []),
  ], [objectTypes.length, selectedId, typeRouting, addButtonLabel, editButtonLabel, deleteButtonLabel, copyButtonLabel, pasteButtonLabel, selectedItem?.type, onOpenMenu, onPressAdd, onPressEdit, onPressCopy, onPressPaste, onPressDelete]);

  const objectTypeOptions = useMemo(() => objectTypes.map((type) => ({
    label: type.name,
    value: type.object_type_id,
  })), [objectTypes]);

  return (
    <MKBox>
      {actionButtons.length > 0 && (
        <MKBox display="flex" flexDirection="row" pb={1}>
          {actionButtons.map((actionButton, idx) => {
            const { label, color, onClick, to, disabled } = actionButton;
            return (
              <MKBox ml={idx > 0 ? 1 : 0}>
                <Button
                  size="small"
                  variant="gradient"
                  color={color}
                  onClick={onClick}
                  to={to}
                  disabled={disabled}
                >
                  {label}
                </Button>
              </MKBox>
            );
          })}
        </MKBox>
      )}
      <MKTable
        columns={headers}
        rows={rows}
        onClickHeader={onSort}
        getRowProps={typeof onClickRow === 'function' ? (row, idx) => ({
          hover: true,
          sx: { cursor: 'pointer' },
          onClick: () => {
            const clickedItem = data[idx];
            return Promise.resolve(onClickRow(clickedItem[idField]))
              .then(() => {
                const targetRoute = typeRouting[clickedItem?.type] || typeRouting?.default;
                if (targetRoute) {
                  navigate(typeRouting[clickedItem?.type] || typeRouting?.default);
                }
              });
          },
        }) : null}
      />
      {!paginationDisabled && (
        <TablePagination
          component="div"
          showFirstButton
          showLastButton
          count={totalCount}
          page={currentPage}
          onPageChange={(e, page) => onPageChange(page)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(e.target.value)}
          rowsPerPageOptions={rowsPerPageOptions}
          labelDisplayedRows={({ from, to, count, page }) => `${from} to ${to} of ${count !== -1 ? count : `more than ${to}`}`}
          labelRowsPerPage="Page Size"
          ActionsComponent={withProps(TablePageActionButtonGroup, { buttonProps: pageButtonProps })}
          sx={{
            '.MuiTablePagination-spacer': {
              display: 'none',
            },
          }}
        />
      )}
      <AnchorMenu
        items={objectTypeOptions}
        onClickItem={onPressAdd}
        anchor={menuAnchor}
        setAnchor={setMenuAnchor}
      />
    </MKBox>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      field: PropTypes.string,
      to: PropTypes.string,
      href: PropTypes.string,
      urlParams: PropTypes.arrayOf(PropTypes.string),
      formatter: PropTypes.func,
      content: PropTypes.node,
      renderContent: PropTypes.func,
      onClick: PropTypes.func,
    }),
  ),
  data: PropTypes.arrayOf(PropTypes.object),
  idField: PropTypes.string,
  selectable: PropTypes.bool,
  objectTypes: PropTypes.object,
  typeRouting: PropTypes.object,
  onSelectRow: PropTypes.func,
  onClickRow: PropTypes.func,
  onPressAdd: PropTypes.func,
  onPressEdit: PropTypes.func,
  onPressDelete: PropTypes.func,
  onPressCopy: PropTypes.func,
  onPressPaste: PropTypes.func,
  totalCount: PropTypes.number,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  rowsPerPage: PropTypes.number,
  onRowsPerPageChange: PropTypes.func,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  paginationDisabled: PropTypes.bool,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  onSort: PropTypes.func,
  pageButtonProps: PropTypes.object,
  addButtonLabel: PropTypes.string,
  editButtonLabel: PropTypes.string,
  deleteButtonLabel: PropTypes.string,
  copyButtonLabel: PropTypes.string,
  pasteButtonLabel: PropTypes.string,
};

DataTable.defaultProps = {
  columns: [],
  data: [],
  idField: 'id',
  selectable: false,
  objectTypes: [],
  typeRouting: {},
  onSelectRow: null,
  onClickRow: null,
  onPressAdd: null,
  onPressEdit: null,
  onPressDelete: null,
  onPressCopy: null,
  onPressPaste: null,
  totalCount: 0,
  currentPage: 0,
  onPageChange: () => {},
  rowsPerPage: 10,
  onRowsPerPageChange: () => {},
  rowsPerPageOptions: [5, 10, 15, 20],
  paginationDisabled: false,
  sortBy: null,
  sortDirection: null,
  onSort: () => {},
  pageButtonProps: {},
  addButtonLabel: 'Add',
  editButtonLabel: 'Edit',
  deleteButtonLabel: 'Delete',
  copyButtonLabel: 'Copy',
  pasteButtonLabel: 'Paste',
};

export default DataTable;
